import {
  Card,
  Title,
  Subtitle,
  LineChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  ProgressBar,
} from "@tremor/react";
import { Truck, MousePointerClick } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TableScroll } from "@/components/ui/TableScroll";
import { KpiCard } from "@/components/ui/KpiCard";
import { deriveLivrari } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { useDrilldown } from "@/components/drilldown/Drilldown";
import { procent, numar, formatLunaKey } from "@/lib/format";

export function LivrariSection() {
  useFilterVersion();
  const { openDrill } = useDrilldown();
  const l = deriveLivrari();

  const chartTrend = l.peLuna.map((m) => ({
    luna: formatLunaKey(m.luna),
    "La timp %": m.procent_la_timp,
  }));
  const topIntarziati = l.topIntarziati.filter((c) => c.intarziere_medie > 0).slice(0, 8);

  return (
    <section>
      <SectionHeader
        id="livrari"
        icon={Truck}
        titlu="9. Performanță livrări — termen promis vs. realizat"
        descriere="Câte comenzi ies la timp, cât întârzie și cine sunt clienții cu cele mai mari întârzieri."
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-4">
        <KpiCard
          accent="emerald"
          eticheta="Livrări la timp"
          valoare={procent(l.procentLaTimp)}
          subtext={`${l.laTimp} din ${l.totalComenzi} comenzi`}
        />
        <KpiCard
          accent="rose"
          eticheta="Comenzi întârziate"
          valoare={String(l.intarziate)}
          subtext={`din ${l.totalComenzi} comenzi`}
        />
        <KpiCard
          accent="amber"
          eticheta="Întârziere medie"
          valoare={`${numar(l.intarziereMedieZile, 1)} zile`}
          subtext="pe toate comenzile filtrate"
        />
        <KpiCard
          accent="blue"
          eticheta="Total comenzi (perioadă)"
          valoare={String(l.totalComenzi)}
          subtext="pe filtrele curente"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="ring-slate-200">
          <Title>Evoluție lunară „la timp %"</Title>
          <Subtitle>Cât de constant respectăm termenele promise</Subtitle>
          <LineChart
            className="mt-4 h-72 w-full"
            data={chartTrend}
            index="luna"
            categories={["La timp %"]}
            colors={["emerald"]}
            valueFormatter={(v) => `${numar(v, 0)}%`}
            showLegend={false}
            curveType="monotone"
            yAxisWidth={44}
            minValue={0}
            maxValue={100}
            noDataText="Fără date"
          />
        </Card>

        <Card className="ring-slate-200">
          <div className="flex items-center justify-between gap-2">
            <div>
              <Title>Clienți cu întârzieri</Title>
              <Subtitle>Întârziere medie (zile) — click pentru comenzi</Subtitle>
            </div>
            <span className="hidden items-center gap-1.5 text-xs text-slate-400 sm:flex">
              <MousePointerClick className="h-3.5 w-3.5" />
              detaliu
            </span>
          </div>
          <TableScroll minWidth="34rem">
            <Table className="mt-3">
              <TableHead>
                <TableRow className="border-slate-200">
                  <TableHeaderCell>Client</TableHeaderCell>
                  <TableHeaderCell className="text-right">Comenzi</TableHeaderCell>
                  <TableHeaderCell className="text-right">La timp %</TableHeaderCell>
                  <TableHeaderCell className="text-right">Întârziere medie</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topIntarziati.length === 0 && (
                  <TableRow>
                    <TableCell className="text-slate-400">Toate comenzile filtrate au ieșit la timp.</TableCell>
                    <TableCell /> <TableCell /> <TableCell />
                  </TableRow>
                )}
                {topIntarziati.map((c) => (
                  <TableRow
                    key={c.client}
                    onClick={() => openDrill({ type: "client", valoare: c.client })}
                    className="cursor-pointer border-slate-100 transition-colors hover:bg-brand-50/60"
                  >
                    <TableCell className="font-medium text-slate-800">{c.client}</TableCell>
                    <TableCell className="text-right tabular">{c.nr_comenzi}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <ProgressBar
                          value={c.procent_la_timp}
                          color={c.procent_la_timp >= 80 ? "emerald" : c.procent_la_timp >= 50 ? "amber" : "rose"}
                          className="w-16"
                        />
                        <span className="tabular text-xs text-slate-500">{procent(c.procent_la_timp)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge size="xs" color={c.intarziere_medie >= 3 ? "rose" : c.intarziere_medie >= 1 ? "amber" : "emerald"}>
                        +{numar(c.intarziere_medie, 1)} zile
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScroll>
        </Card>
      </div>
    </section>
  );
}
